import React, { useState, useRef } from 'react';
import ModuleTopExtras from '../ModuleTopExtras';
import { useLanguage } from '../../i18n/LanguageContext';
import { API_ENDPOINTS, apiRequest } from '../../config/api';
import { mixedLanguageWordCount } from '../../utils/textUtils';
import { AdProcessingBanner } from '../ads/Ads';
import './Modules.css';

// Detect backend OCR notice placeholder to prevent accidental grading on placeholder text
const containsOcrNotice = (text: string): boolean => {
  if (!text) return false;
  const t = text.toLowerCase();
  return t.includes('[ocr notice]') || t.includes('automatic text extraction from this document was unsuccessful');
};

const examTypes = [
  'DSE',
  'IELTS',
  'TOEFL',
  'GRE',
  'SAT',
  'Other'
] as const;

type ExamType = typeof examTypes[number];
type TopicMode = 'text' | 'visual';

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_ESSAY_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

const WritingGradedModule: React.FC = () => {
  const { t } = useLanguage();
  const [currentView, setCurrentView] = useState<'write' | 'results' | 'report'>('write');
  const [examType, setExamType] = useState<ExamType>('DSE');
  const [otherPurpose, setOtherPurpose] = useState('');

  // Topic — either typed text or an uploaded image (for prompts with a diagram/graph).
  const [topicMode, setTopicMode] = useState<TopicMode>('text');
  const [topic, setTopic] = useState('');
  const [topicImage, setTopicImage] = useState<File | null>(null);
  const [topicPreview, setTopicPreview] = useState<string | null>(null);

  // Essay — pasted/typed, or filled from an uploaded file via text extraction.
  const [writingText, setWritingText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [isGrading, setIsGrading] = useState(false);
  const [gradingProgress, setGradingProgress] = useState<number>(0);
  const [gradingMessage, setGradingMessage] = useState<string>('');
  const [report, setReport] = useState<any>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const topicImageRef = useRef<HTMLInputElement>(null);

  const handleTopicImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      alert('Please select a PNG or JPG image file.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      alert('Image file size must be less than 10MB.');
      return;
    }

    setTopicImage(file);
    setTopicPreview(URL.createObjectURL(file));
  };

  const removeTopicImage = () => {
    setTopicImage(null);
    if (topicPreview) URL.revokeObjectURL(topicPreview);
    setTopicPreview(null);
    if (topicImageRef.current) topicImageRef.current.value = '';
  };

  // Upload an essay file and put the EXTRACTED text into the "Your Essay" box.
  // Images (photo of an essay) are read visually; documents use embedded text.
  const handleEssayFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_ESSAY_FILE_SIZE) {
      alert('File size must be less than 25MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
    setIsUploading(true);

    try {
      let extracted = '';

      if (file.type.startsWith('text') || file.name.toLowerCase().endsWith('.txt')) {
        extracted = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsText(file);
        });
      } else {
        const mode = file.type.startsWith('image/') ? 'vision' : 'text';
        const form = new FormData();
        form.append('file', file);
        form.append('mode', mode);
        const response = await apiRequest<{ extracted_text: string }>(API_ENDPOINTS.EXTRACT_DOCUMENT, {
          method: 'POST',
          body: form,
          timeout: 10 * 60 * 1000,
        });
        extracted = response.data?.extracted_text || '';
      }

      if (!extracted || containsOcrNotice(extracted)) {
        alert('Could not extract any usable text from that file. Please try another file or paste the essay.');
      } else {
        setWritingText((prev) => (prev ? `${prev}\n\n${extracted}` : extracted));
      }
    } catch (err) {
      console.error('Essay extraction error', err);
      const serverMsg = err instanceof Error ? err.message : '';
      alert(
        (serverMsg && serverMsg !== 'API request failed' ? `${serverMsg}\n\n` : '') +
          'Could not extract text from that file. Please paste the essay instead.'
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleGradeWriting = async () => {
    if (!writingText.trim()) return;
    if (containsOcrNotice(writingText)) {
      alert('An extraction notice was detected in the essay. Please paste the real essay text before grading.');
      return;
    }

    setIsGrading(true);
    setGradingProgress(0);
    setGradingMessage('Preparing to grade your writing...');
    setReport(null);

    const progressInterval = setInterval(() => {
      setGradingProgress((prev) => (prev >= 90 ? 90 : prev + 10));
    }, 300);

    try {
      let requestBody: BodyInit;
      if (topicMode === 'visual' && topicImage) {
        // FormData so the topic image can be read by the vision model.
        const formData = new FormData();
        formData.append('content', writingText);
        formData.append('exam_type', examType);
        formData.append('purpose', otherPurpose);
        formData.append('topic', topic);
        formData.append('topic_image', topicImage);
        requestBody = formData;
      } else {
        requestBody = JSON.stringify({
          content: writingText,
          exam_type: examType,
          purpose: otherPurpose,
          topic,
        });
      }

      const response = await apiRequest<any>(API_ENDPOINTS.WRITING_GRADE, {
        method: 'POST',
        body: requestBody,
        timeout: 10 * 60 * 1000,
      });

      clearInterval(progressInterval);
      setGradingProgress(100);
      setGradingMessage('Generating report...');

      if (response.data) {
        setReport(response.data);
        setCurrentView('report');
      } else {
        setGradingMessage(response.error || 'Failed to get feedback');
        alert(response.error || 'Failed to get feedback');
      }
    } catch (err) {
      setGradingMessage('Network error. Please try again.');
      console.error('Grading failed', err);
      alert('Network error. Please try again.');
    } finally {
      setIsGrading(false);
      setTimeout(() => setGradingProgress(0), 1000);
    }
  };

  const restart = () => {
    setExamType('DSE');
    setOtherPurpose('');
    setTopicMode('text');
    setTopic('');
    setTopicImage(null);
    if (topicPreview) URL.revokeObjectURL(topicPreview);
    setTopicPreview(null);
    setWritingText('');
    setSelectedFile(null);
    setReport(null);
    setCurrentView('write');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (topicImageRef.current) topicImageRef.current.value = '';
  };

  const downloadReport = () => {
    if (!report) return;

    const topicSummary = topicMode === 'visual' && topicImage
      ? `[Image] ${topicImage.name}${topic ? ` — ${topic}` : ''}`
      : topic || 'Not specified';

    const reportContent = `WRITING GRADED MODULE REPORT
=====================================

WRITING CONTEXT
---------------
Exam Type: ${examType}
Purpose: ${examType === 'Other' ? otherPurpose : examType}
Topic: ${topicSummary}

SCORE
-----
${report.score}

MISTAKES
--------
${Array.isArray(report.mistakes) && report.mistakes.length > 0
  ? report.mistakes.map((mistake: string, idx: number) => `${idx + 1}. ${mistake}`).join('\n')
  : String(report.mistakes)}

SUGGESTIONS
-----------
${Array.isArray(report.suggestions)
  ? report.suggestions.map((s: string, idx: number) => `${idx + 1}. ${s}`).join('\n')
  : String(report.suggestions)}

FEEDBACK
--------
${report.feedback}

REVISED VERSION
---------------
${report.revised}

ORIGINAL WRITING
----------------
${writingText}

---
Generated on: ${new Date().toLocaleString()}
StudyMind Writing Graded Module
`;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `writing-report-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const topicOption = (mode: TopicMode, icon: string, label: string, sub: string) => (
    <button
      type="button"
      className={`ext-option ${topicMode === mode ? 'active' : ''}`}
      aria-pressed={topicMode === mode}
      onClick={() => setTopicMode(mode)}
    >
      <span className="ext-icon">{icon}</span>
      <span className="ext-text">
        <span className="ext-label">{label}</span>
        <span className="ext-sub">{sub}</span>
      </span>
    </button>
  );

  return (
    <div className="module-container writing-module">
      <div className="module-header">
        <span className="module-icon">✍️</span>
        <h2 className="module-title">{t('moduleHeaders.writing.title')}</h2>
        <p className="module-subtitle">{t('moduleHeaders.writing.subtitle')}</p>
      </div>

      <ModuleTopExtras moduleId="writing" />

      <div className="module-content">
        {currentView === 'write' && (
          <div className="content-section">
            <div className="section-header">
              <h3>Submit Your Writing</h3>
              <p>Select an exam type, add the topic, then provide your essay.</p>
            </div>

            {/* Exam Type Selection */}
            <div className="form-group">
              <label className="form-label" htmlFor="exam-type-select">Exam / Writing Type</label>
              <select
                id="exam-type-select"
                className="form-input form-select"
                value={examType}
                onChange={(e) => setExamType(e.target.value as ExamType)}
              >
                {examTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* When Other selected, ask for purpose */}
            {examType === 'Other' && (
              <div className="form-group">
                <label className="form-label" htmlFor="other-purpose">Writing Purpose</label>
                <input
                  type="text"
                  id="other-purpose"
                  className="form-input"
                  placeholder="e.g., Scholarship motivation letter"
                  value={otherPurpose}
                  onChange={(e) => setOtherPurpose(e.target.value)}
                />
              </div>
            )}

            {/* Topic — text or visual */}
            <div className="form-group">
              <label className="form-label">Topic</label>
              <div className="ext-toggle-wrap">
                <div className="ext-toggle" role="group" aria-label="Topic input mode">
                  {topicOption('text', '📄', 'Text', 'Type the prompt')}
                  {topicOption('visual', '🖼️', 'Visual', 'Upload a photo of the prompt')}
                </div>
                <p className="ext-hint">
                  💡 If your topic involves a <strong>diagram, graph, chart, table or figure</strong>,
                  please choose <strong>Visual</strong> and upload a clear image so the AI can read it.
                </p>
              </div>

              {topicMode === 'text' ? (
                <textarea
                  id="writing-topic"
                  className="content-textarea"
                  rows={3}
                  style={{ marginTop: 12 }}
                  placeholder="Briefly describe your topic or prompt..."
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                />
              ) : (
                <div style={{ marginTop: 12 }}>
                  <input
                    type="file"
                    ref={topicImageRef}
                    style={{ display: 'none' }}
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    onChange={handleTopicImageChange}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => topicImageRef.current?.click()}
                  >
                    {topicImage ? 'Replace topic image' : 'Upload topic image'}
                  </button>
                  {topicImage && topicPreview && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={topicPreview}
                          alt="Topic preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '300px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            objectFit: 'contain',
                          }}
                        />
                        <button
                          type="button"
                          onClick={removeTopicImage}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'rgba(220, 53, 69, 0.85)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            cursor: 'pointer',
                            fontSize: 14,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                      <div className="file-info" style={{ marginTop: 8 }}>
                        <span className="file-name">{topicImage.name}</span>
                        <span className="file-details"> ({(topicImage.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Upload Essay — fills the essay box with extracted text */}
            <div className="form-group">
              <label className="form-label">Upload Essay (optional)</label>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".txt,.docx,.pdf,.ppt,.pptx,.png,.jpg,.jpeg"
                onChange={handleEssayFile}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? 'Extracting text…' : 'Upload Essay'}
              </button>
              <p className="ext-hint" style={{ marginTop: 8 }}>
                ✍️ If your essay is <strong>handwritten</strong> (a photo or scan), please first use
                <strong> OCR Document</strong> to convert it into digital text, then upload the text
                file or paste the text into “Your Essay”.
              </p>
              {selectedFile && (
                <div className="file-info">
                  <span className="file-name">{selectedFile.name}</span>
                </div>
              )}
            </div>

            {/* Essay Textarea */}
            <div className="form-group">
              <label className="form-label" htmlFor="writing-text">Your Essay</label>
              <textarea
                id="writing-text"
                className="content-textarea"
                rows={12}
                placeholder="Paste or write your essay here, or upload a file to fill this box..."
                value={writingText}
                onChange={(e) => setWritingText(e.target.value)}
              />
              {containsOcrNotice(writingText) && (
                <div className="credit-warning" style={{ color: '#dc3545', marginTop: '6px' }}>
                  ⚠️ An extraction notice was detected. Please paste the real essay text.
                </div>
              )}
            </div>

            {/* Word count */}
            {writingText.trim() && (
              <div className="word-count-info">
                <span>{mixedLanguageWordCount(writingText).toLocaleString()} words</span>
              </div>
            )}

            {/* Grade Button */}
            <div className="generation-actions">
              <button
                className="btn btn-primary"
                disabled={isGrading || !writingText.trim() || containsOcrNotice(writingText)}
                onClick={handleGradeWriting}
              >
                {isGrading ? 'Grading...' : 'Get Grade & Feedback'}
              </button>
            </div>
          </div>
        )}

        {/* Progress Bar when grading */}
        {isGrading && (
          <div className="content-section">
            <div className="section-header">
              <h3>Grading in Progress</h3>
            </div>
            <AdProcessingBanner active={isGrading} />
            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${gradingProgress}%` }}></div>
                <div className="progress-percentage">{gradingProgress}%</div>
              </div>
              <div className="progress-message">{gradingMessage}</div>
            </div>
          </div>
        )}

        {/* Report Page */}
        {currentView === 'report' && report && (
          <div className="content-section">
            <div className="section-header">
              <h3>Writing Report</h3>
            </div>
            <div className="results-container">
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>📋 Writing Context</h4>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <div>
                    <strong style={{ color: '#495057' }}>Exam Type:</strong>
                    <span style={{ marginLeft: '0.5rem', color: '#6c757d' }}>{examType}</span>
                  </div>
                  {(otherPurpose || examType !== 'Other') && (
                    <div>
                      <strong style={{ color: '#495057' }}>Purpose:</strong>
                      <span style={{ marginLeft: '0.5rem', color: '#6c757d' }}>
                        {examType === 'Other' ? otherPurpose : examType}
                      </span>
                    </div>
                  )}
                  {(topic || (topicMode === 'visual' && topicImage)) && (
                    <div>
                      <strong style={{ color: '#495057' }}>Topic:</strong>
                      {topicMode === 'visual' && topicImage ? (
                        <span style={{ marginLeft: '0.5rem', color: '#6c757d' }}>
                          🖼️ {topicImage.name}{topic ? ` — ${topic}` : ' (read from image)'}
                        </span>
                      ) : (
                        <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d', fontStyle: 'italic' }}>{topic}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <p><strong>Score:</strong> {report.score}</p>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Mistakes:</strong>
                {report.mistakes && Array.isArray(report.mistakes) && report.mistakes.length > 0 ? (
                  <ul style={{ marginLeft: '1rem' }}>
                    {report.mistakes.map((mistake: string, idx: number) => (
                      <li key={idx} style={{ marginBottom: '0.25rem' }}>{mistake}</li>
                    ))}
                  </ul>
                ) : (
                  <p>{String(report.mistakes)}</p>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <strong>Suggestions:</strong>
                {Array.isArray(report.suggestions) ? (
                  <ul style={{ marginLeft: '1rem' }}>
                    {report.suggestions.map((s: string, idx: number) => (
                      <li key={idx}>{s}</li>
                    ))}
                  </ul>
                ) : (
                  <p>{String(report.suggestions)}</p>
                )}
              </div>

              <p><strong>Feedback:</strong> {report.feedback}</p>
              <p><strong>Revised Version:</strong></p>
              <pre style={{ background: '#f9fafb', padding: '1em', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>{report.revised}</pre>
            </div>
            <div className="results-container" style={{ marginTop: '2em' }}>
              <h4>Original Writing</h4>
              <pre style={{ background: '#f3f4f6', padding: '1em', borderRadius: '8px', whiteSpace: 'pre-wrap' }}>{writingText}</pre>
            </div>
            <div className="generation-actions">
              <button className="btn btn-secondary" onClick={restart}>Grade Another</button>
              <button className="btn btn-primary" onClick={downloadReport}>Download Report</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WritingGradedModule;
